import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import './NotificationModal.css';

function NotificationModal({ open, onClose, title, body, from, meta }) {
  const navigate = useNavigate();
  
  // Lock body scroll while modal is open
  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow || '';
    };
  }, [open]);

  if (!open) return null;

  const handleProductClick = () => {
    if (meta?.productId) {
      navigate(`/product/${meta.productId}`);
      onClose();
    }
  };

  return ReactDOM.createPortal(
    <AnimatePresence>
      <motion.div 
        className="notif-modal-backdrop" 
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        <motion.div 
          className="notif-modal" 
          onClick={e => e.stopPropagation()}
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          transition={{ duration: 0.3, type: "spring", stiffness: 300, damping: 25 }}
        >
          {/* Decorative header accent */}
          <div className="notif-modal-accent"></div>
          
          <div className="notif-modal-header">
            <div className="notif-header-content">
              <div className="notif-icon">{meta?.productId ? '🌱' : '🔔'}</div>
              <div>
                <h3 className="notif-title">{title || 'Notification'}</h3>
                {from && (
                  <div className="notif-from">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ marginRight: 4, opacity: 0.6 }}>
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="currentColor"/>
                    </svg>
                    {from}
                  </div>
                )}
              </div>
            </div>
            <motion.button 
              className="notif-close" 
              onClick={onClose}
              whileHover={{ scale: 1.1, rotate: 90 }}
              whileTap={{ scale: 0.9 }}
              transition={{ duration: 0.2 }}
            >
              ×
            </motion.button>
          </div>
          
          <div className="notif-modal-body">
            <div className="notif-message-content">{body || ''}</div>
            
            {/* Product Card */}
            {meta?.productId && (
              <motion.div 
                className="notif-product-card"
                onClick={handleProductClick}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{
                  marginTop: 16,
                  padding: 16,
                  background: '#f9fafb',
                  borderRadius: 12,
                  display: 'flex',
                  gap: 12,
                  cursor: 'pointer',
                  border: '2px solid #e5e7eb',
                  transition: 'all 0.2s ease'
                }}
              >
                <img 
                  src={meta.productImage || '/default-plant.png'} 
                  alt={meta.productName}
                  style={{
                    width: 80,
                    height: 80,
                    objectFit: 'cover',
                    borderRadius: 8,
                    background: '#fff'
                  }}
                />
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                  <h4 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#1a1a1a' }}>
                    {meta.productName}
                  </h4>
                  <p style={{ margin: '4px 0 0 0', fontSize: '1.1rem', fontWeight: 700, color: '#7a9c3a' }}>
                    ₱{meta.productPrice?.toFixed(2)}
                  </p>
                  <p style={{ margin: '6px 0 0 0', fontSize: '0.85rem', color: '#7a9c3a', fontWeight: 500 }}>
                    Click to view →
                  </p>
                </div>
              </motion.div>
            )}
          </div>
          
          <div className="notif-modal-actions">
            {meta?.productId ? (
              <motion.button 
                className="notif-btn-view" 
                onClick={handleProductClick}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                style={{
                  background: '#7a9c3a',
                  color: '#fff',
                  padding: '12px 24px',
                  borderRadius: 8,
                  border: 'none',
                  fontWeight: 600,
                  cursor: 'pointer',
                  fontSize: '0.95rem'
                }}
              >
                View Product
              </motion.button>
            ) : (
              <motion.button 
                className="notif-btn-close" 
                onClick={onClose}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                Got it
              </motion.button>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}

export default NotificationModal;
